import { Link } from 'react-router';
import { stillExpected } from '@tahi/core';
import { MenuCard, Money } from '../../../ui';
import { Banner } from '../../../ui/Banner';
import { sourceLabel } from '../../spending/format';
import {
  ComingInCard,
  DueBeforePayday,
  OwedToYouCard,
  SafeToSpendHero,
  SavingsCard,
  YourMoneyCard,
} from '../components/MoneyCards';
import { copy } from '../copy';
import {
  accountStatusesOf,
  cashFlowOf,
  currentRange,
  dueBeforePaydayOf,
  duePaydaysOf,
  fundsOn,
  liveSources,
  owedToYouOf,
  receivablesOf,
} from '../derive';
import { dayShort } from '../format';
import { useBudgetToday } from '../hooks';
import { ACCENT_LINK, FOCUS, H1, QUIET_LINK, SCREEN } from '../styles';

/**
 * v0.5 (Ivan, 2026-11-03: "this is a mess"): one hero (Safe to spend until payday), then Your
 * money, what's due before payday and what's coming in. Cash flow is one tap away.
 */
export function BudgetDashboard() {
  const s = useBudgetToday();
  if (!s) {
    return (
      <main className={SCREEN}>
        <h1 className={H1}>{copy.moduleName}</h1>
        <p className="text-muted">{copy.loading}</p>
      </main>
    );
  }
  const due = duePaydaysOf(s)[0];
  const limits = fundsOn(s);
  const expected = stillExpected(s.expected, s.today);
  const owed = receivablesOf(s).some((r) => !r.settled);
  // v0.7.5 menu values, all from the snapshot this screen already has.
  const sources = liveSources(s);
  const cutoffLeft = cashFlowOf(s, currentRange(s, 'cutoff'), 'cutoff').whatsLeftMinor;
  const statuses = accountStatusesOf(s);
  const accountsTotal = statuses.reduce((sum, a) => sum + a.balanceMinor, 0);
  const owedMinor = owedToYouOf(s);
  const liveFunds = s.funds.filter((f) => f.deletedAt === null).length;
  const accountName = (id: string | null) =>
    (id && s.accounts.find((a) => a.id === id)?.name) || null;
  const recent = s.entries
    .filter((e) => e.kind !== 'transfer' && e.date <= s.today)
    .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
    .slice(0, 5);
  const fundNames = new Map(s.funds.map((f) => [f.id, f.name]));

  return (
    <main className={SCREEN}>
      <h1 className={H1}>{copy.moduleName}</h1>
      {due ? (
        <Banner>
          <div className="flex items-center justify-between gap-3">
            <span>{copy.paydayBanner(due.defaultAmountMinor)}</span>
            <Link
              to={`/m/budget/payday/${due.sourceId}/${due.nominalDate}`}
              className={ACCENT_LINK}
            >
              {copy.answer}
            </Link>
          </div>
        </Banner>
      ) : null}
      {sources.length === 0 ? (
        <p className="text-muted">
          {copy.noIncomeYet}{' '}
          <Link to="/m/budget/income/new?kind=salary" className={QUIET_LINK}>
            {copy.addSalary}
          </Link>
        </p>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-4">
          <SafeToSpendHero s={s} />
          <YourMoneyCard s={s} />
          <SavingsCard s={s} />
        </div>
        <div className="flex flex-col gap-4">
          <DueBeforePayday items={dueBeforePaydayOf(s)} />
          {expected.length > 0 ? (
            <ComingInCard items={expected} accountName={accountName} today={s.today} />
          ) : null}
          {owed ? <OwedToYouCard s={s} /> : null}
        </div>
      </div>

      <MenuCard
        label={copy.dashboardNav}
        items={[
          {
            to: '/m/budget/cashflow',
            label: copy.cashFlow,
            icon: 'cashflow',
            amountMinor: sources.length > 0 ? cutoffLeft : null,
            amountSuffix: copy.menuLeft,
          },
          {
            to: '/m/budget/income',
            label: copy.income,
            icon: 'income',
            value: sources.length > 0 ? copy.sourcesCount(sources.length) : null,
          },
          {
            to: '/m/budget/accounts',
            label: copy.accounts,
            icon: 'accounts',
            amountMinor: statuses.length > 0 ? accountsTotal : null,
          },
          expected.length === 0 && {
            to: '/m/budget/expected/new',
            label: copy.addExpected,
            icon: 'add',
          },
          {
            to: '/m/budget/owed',
            label: copy.owedToYou,
            icon: 'owed',
            amountMinor: owedMinor > 0 ? owedMinor : null,
          },
          limits && {
            to: '/m/budget/funds',
            label: copy.funds,
            icon: 'limits',
            value: liveFunds > 0 ? copy.limitsCount(liveFunds) : null,
          },
          { to: '/m/budget/settings', label: copy.settings, icon: 'settings' },
        ]}
      />

      {recent.length > 0 ? (
        <section className="flex flex-col gap-2">
          <h2 className="text-[20px] font-bold text-fg">{copy.recent}</h2>
          <ul aria-label={copy.recent} className="divide-y divide-divider">
            {recent.map((e) => (
              <li key={e.id}>
                <Link
                  to={e.kind === 'income' ? '/m/budget/income' : `/m/spending/entries/${e.id}`}
                  className={`flex min-h-11 items-center justify-between gap-3 ${FOCUS}`}
                >
                  <span className="min-w-0 text-fg">
                    {e.note ?? sourceLabel(e.sourceModule)} · {dayShort(e.date)}
                    <span className="text-sm text-muted">
                      {' '}
                      · {sourceLabel(e.sourceModule)}
                      {limits && e.fundId && fundNames.has(e.fundId)
                        ? ` · ${fundNames.get(e.fundId)}`
                        : ''}
                      {e.accountId && accountName(e.accountId)
                        ? ` · ${accountName(e.accountId)}`
                        : ''}
                    </span>
                  </span>
                  <span className={`shrink-0 ${e.kind === 'income' ? 'text-success' : 'text-fg'}`}>
                    {e.kind === 'income' ? '+' : ''}
                    <Money minor={e.amountMinor} cents="auto" animate={false} />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </main>
  );
}
